import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import EmployeeLayout from "./EmployeeLayout";

import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  TextField,
  Alert,
  Divider,
  Stack,
  Chip,
} from "@mui/material";

import {
  Download,
  AccountBalanceWallet,
  ReceiptLong,
} from "@mui/icons-material";

const EARNINGS_KEYS = ["basic", "hra", "conveyance", "special_allowance", "medical", "bonus"];
const DEDUCTION_KEYS = ["pf", "esi", "professional_tax", "tds"];

const LABELS = {
  basic: "BASIC",
  hra: "HOUSE RENT ALLOWANCE (HRA)",
  conveyance: "TRAVELING / CONVEYANCE",
  special_allowance: "SPECIAL ALLOWANCE",
  medical: "MEDICAL",
  bonus: "BONUS",

  pf: "PROVIDENT FUND (PF)",
  esi: "ESI",
  professional_tax: "PROFESSIONAL TAX",
  tds: "INCOME TAX (TDS)",
};

function inr(n) {
  return `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
}

export default function EmployeeSalaryView() {
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const [slipUrl, setSlipUrl] = useState("");
  const [error, setError] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const employeeId = user?.id;

  /* ================= LOAD SALARY STRUCTURE ================= */
  useEffect(() => {
    const loadSalary = async () => {
      try {
        const res = await api.get(`/salary/structure/${employeeId}`);
        setSalary(res.data);
        setError("");
      } catch {
        setError("Salary structure not available");
        setSalary(null);
      } finally {
        setLoading(false);
      }
    };
    if (employeeId) loadSalary();
  }, [employeeId]);

  /* ================= CALCULATIONS (Gross / Deductions / Net) ================= */
  const summary = useMemo(() => {
    if (!salary) return { gross: 0, deductions: 0, net: 0 };

    const gross = EARNINGS_KEYS.reduce((sum, k) => sum + Number(salary?.[k] || 0), 0);
    const deductions = DEDUCTION_KEYS.reduce((sum, k) => sum + Number(salary?.[k] || 0), 0);
    const net = gross - deductions;

    return { gross, deductions, net };
  }, [salary]);

  /* ================= VIEW SALARY SLIP (HR GENERATED) ================= */
  const handleGetSlip = async () => {
    if (!month || !year) return alert("Month aur Year select karo");

    try {
      const res = await api.get(`/salary/slip/view/${employeeId}/${month}/${year}`);
      setSlipUrl(res.data.slip_url);
    } catch (err) {
      setSlipUrl("");
      alert("HR ne abhi salary slip generate nahi ki hai");
    }
  };

  if (loading) {
    return (
      <EmployeeLayout>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          My Salary
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* ================= Salary Structure ================= */}
        {salary && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <AccountBalanceWallet color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Salary Structure (Earnings & Deductions)
              </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Summary chips */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <Chip label={`Gross: ${inr(summary.gross)}`} />
              <Chip label={`Deductions: ${inr(summary.deductions)}`} color="warning" />
              <Chip label={`Net: ${inr(summary.net)}`} color="success" />
            </Stack>

            {/* Earnings Table */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Earnings
            </Typography>

            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                  <TableCell><b>Component</b></TableCell>
                  <TableCell align="right"><b>Amount (₹)</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {EARNINGS_KEYS.map((key) => {
                  const value = Number(salary?.[key] || 0);
                  if (!value) return null;

                  return (
                    <TableRow key={key}>
                      <TableCell>{LABELS[key] || key.replace(/_/g, " ").toUpperCase()}</TableCell>
                      <TableCell align="right">{inr(value)}</TableCell>
                    </TableRow>
                  );
                })}

                {EARNINGS_KEYS.every((k) => Number(salary?.[k] || 0) === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No earnings components found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Deductions Table (ESI included here) */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Deductions (PF / ESI / Tax)
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                  <TableCell><b>Deduction</b></TableCell>
                  <TableCell align="right"><b>Amount (₹)</b></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {DEDUCTION_KEYS.map((key) => {
                  const value = Number(salary?.[key] || 0);
                  if (!value) return null;

                  return (
                    <TableRow key={key}>
                      <TableCell>
                        {LABELS[key] || key.replace(/_/g, " ").toUpperCase()}
                        {key === "esi" && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                            (ESI deduction will appear in payslip as well)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{inr(value)}</TableCell>
                    </TableRow>
                  );
                })}

                {DEDUCTION_KEYS.every((k) => Number(salary?.[k] || 0) === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No deductions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* ================= Salary Slip (VIEW ONLY) ================= */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <ReceiptLong color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Salary Slip
            </Typography>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Month"
              type="number"
              size="small"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              inputProps={{ min: 1, max: 12 }}
              fullWidth
            />

            <TextField
              label="Year"
              type="number"
              size="small"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              fullWidth
            />

            <Button
              variant="contained"
              onClick={handleGetSlip}
              sx={{ minWidth: 180 }}
            >
              View Salary Slip
            </Button>
          </Stack>

          {slipUrl && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                href={slipUrl}
                target="_blank"
              >
                Download Salary Slip
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </EmployeeLayout>
  );
}
